import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchActiveThreads, getTaskByThread } from '../lib/api';
import { routes } from '../lib/routes';
import { useThreadStore } from '../stores';
import { migrateDraft, pendingThreadDraftKey, threadDraftKey } from './useComposerDraft';

interface ThreadRouteInput {
  guildId?: string;
  channelId?: string;
  /** The thread the current route names, if it names one. */
  threadId?: string;
  /** Joins a channel's realtime stream; a thread is a channel of its own. */
  subscribe: (channelId: string) => void;
}

export interface ThreadRoute {
  /** The message the channel should mark as the one whose thread is open. */
  activeMessageId: string | null;
  /** Set while a thread has been asked for but no reply has created it yet. */
  pendingMessageId: string | null;
  open: (messageId: string) => void;
  created: (threadId: string) => void;
  /** The panel's own close control. */
  close: () => void;
  /** The panel leaving for a reason other than its close control. */
  dismiss: () => void;
}

/**
 * Which thread the channel is showing, and the four ways that changes.
 *
 * A thread only exists on the server once a reply creates it, so asking to
 * reply to a message that has none puts the panel up against a pending message
 * id and leaves the route on the channel until the first reply lands. Nothing
 * about leaving that panel touches the route either, since the channel is
 * already the entry on screen.
 *
 * Closing and dismissing both land on the channel and differ in what they leave
 * behind in the history. Closing is deliberate — the member pressed the control
 * that means "I am done here" — so it pushes, and Back brings the thread back.
 * A dismissal is incidental: the member pressed something in the channel the
 * thread was covering, or hit Escape. It therefore goes back to the channel the
 * thread was opened over, rather than pushing or replacing: replacing would
 * leave the channel entry duplicated, one above the other, and the next Back
 * would move nowhere.
 */
export function useThreadRoute({ guildId, channelId, threadId, subscribe }: ThreadRouteInput): ThreadRoute {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  /**
   * The thread whose history entry we pushed over the channel, and which Back
   * therefore leaves for the channel. A thread the member arrived at any other
   * way — a cold deep link, a shared URL — sits over nothing we know.
   */
  const pushedThreadId = useRef<string | null>(null);
  /**
   * The thread the route names right now. An `open` waiting on the thread
   * listing outlives the render it was called from — a dismissal in the same
   * gesture can traverse the history out from under it — so what it decides
   * when the listing lands has to be decided against the entry that is actually
   * on screen by then, not the one that was when the member clicked.
   */
  const routeThreadId = useRef(threadId);
  routeThreadId.current = threadId;

  useEffect(() => {
    if (threadId) subscribe(threadId);
  }, [threadId, subscribe]);

  const open = useCallback(
    (messageId: string) => {
      if (!channelId || !guildId) return;
      setActiveMessageId(messageId);
      const startPending = () => {
        // Whatever thread the route still names is not this message's, and it
        // is on its way out rather than somewhere to come back to.
        if (routeThreadId.current) {
          navigate(routes.channel(guildId, channelId), { replace: true });
          pushedThreadId.current = null;
        }
        setPendingMessageId(messageId);
      };
      fetchActiveThreads(channelId)
        .then(({ threads }) => {
          const existing = threads.find(t => t.message_id === messageId);
          if (!existing) {
            startPending();
            return;
          }
          // The thread may have been created by someone else's reply while a
          // draft was sitting under the pending key. This is the moment both
          // ids are known, and the composer that could read the pending key is
          // about to be replaced by the one keyed on the real thread.
          migrateDraft(pendingThreadDraftKey(messageId), threadDraftKey(existing.id));
          subscribe(existing.id);
          const leaving = routeThreadId.current;
          const openExisting = (isTaskThread: boolean) => {
            const search = new URLSearchParams(location.search);
            if (isTaskThread) search.set('thread', existing.id);
            const target = isTaskThread
              ? { pathname: routes.channel(guildId, channelId), search: `?${search.toString()}` }
              : routes.thread(guildId, channelId, existing.id);
            if (leaving) {
              pushedThreadId.current = pushedThreadId.current === leaving ? existing.id : null;
              navigate(target, { replace: true });
              return;
            }
            pushedThreadId.current = existing.id;
            navigate(target);
          };
          // Task threads share the URL-backed panel model; ordinary message
          // threads retain their channel-nested route.
          getTaskByThread(existing.id).then(() => openExisting(true)).catch(() => openExisting(false));
        })
        .catch(startPending);
    },
    [channelId, guildId, location.search, navigate, subscribe],
  );

  const created = useCallback(
    (createdThreadId: string) => {
      if (!channelId || !guildId) return;
      setPendingMessageId(null);
      subscribe(createdThreadId);
      pushedThreadId.current = createdThreadId;
      navigate(routes.thread(guildId, channelId, createdThreadId));
      useThreadStore.getState().loadThreads(channelId);
    },
    [channelId, guildId, location.search, navigate, subscribe],
  );

  const clearPanel = useCallback(() => {
    setPendingMessageId(null);
    setActiveMessageId(null);
  }, []);

  const close = useCallback(() => {
    clearPanel();
    if (!threadId || !channelId || !guildId) return;
    navigate(routes.channel(guildId, channelId));
  }, [clearPanel, threadId, channelId, guildId, navigate]);

  const dismiss = useCallback(() => {
    clearPanel();
    if (!threadId || !channelId || !guildId) return;
    if (pushedThreadId.current === threadId) {
      // The entry is given up before the traversal is asked for, not after it
      // lands: the panel stays on screen until the POP re-renders it, and a
      // second dismissal arriving in that window would otherwise ask for a
      // second traversal and take the member an entry below the channel.
      pushedThreadId.current = null;
      navigate(-1);
      return;
    }
    navigate(routes.channel(guildId, channelId), { replace: true });
  }, [clearPanel, threadId, channelId, guildId, navigate]);

  return { activeMessageId, pendingMessageId, open, created, close, dismiss };
}
